import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Scroll, TreeStructure, Handshake, Bank, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AIChatWidget from "../components/AIChatWidget";
import Pricing from "../components/Pricing";
import BookingPayPal from "../components/BookingPayPal";
import { api, formatApiError } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1706200637521-b446bd05df0d";
const APPROACH_IMG = "https://images.unsplash.com/photo-1537735319956-df7db4b6a4e9";
const DOC_IMG = "https://images.unsplash.com/photo-1473186505569-9c61870c11f9";

const ease = [0.22, 1, 0.36, 1];

const heroLines = ["Preserve what", "matters. Protect", "those you love."];

const services = [
  { icon: Scroll, title: "Wills & Testaments", desc: "Clear, legally sound wills that ensure your wishes are honored precisely as intended." },
  { icon: TreeStructure, title: "Living Trusts", desc: "Avoid probate and protect assets with revocable and irrevocable trust structures." },
  { icon: Handshake, title: "Power of Attorney", desc: "Appoint trusted decision-makers for your financial and healthcare matters." },
  { icon: Bank, title: "Probate Administration", desc: "Compassionate, thorough guidance through estate settlement and administration." },
];

const chapters = [
  { n: "01", title: "Listen first", body: "Every legacy is personal. We begin with your story, your family, and your intentions — never a template." },
  { n: "02", title: "Design with care", body: "We craft a plan tailored to your assets and wishes, engineered to hold firm across generations." },
  { n: "03", title: "Protect for life", body: "Your plan evolves as life does. We review, adjust, and stand beside you for the long term." },
];

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease, delay }}>
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const [booking, setBooking] = useState({ name: "", email: "", phone: "", service: "Wills & Testaments", preferred_date: "", message: "" });
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [bLoading, setBLoading] = useState(false);
  const [cLoading, setCLoading] = useState(false);
  const [payCfg, setPayCfg] = useState(null);

  useEffect(() => { api.get("/payments/packages").then((r) => setPayCfg(r.data)).catch(() => {}); }, []);

  const getBooking = () => {
    if (!booking.name || !booking.email || !booking.service || !booking.preferred_date) return null;
    return booking;
  };
  const resetBooking = () => setBooking({ name: "", email: "", phone: "", service: "Wills & Testaments", preferred_date: "", message: "" });

  const submitBooking = async (e) => {
    e.preventDefault();
    setBLoading(true);
    try {
      await api.post("/appointments", booking);
      toast.success("Consultation request sent. We'll be in touch shortly.");
      setBooking({ name: "", email: "", phone: "", service: "Wills & Testaments", preferred_date: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBLoading(false); }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setCLoading(true);
    try {
      await api.post("/contacts", contact);
      toast.success("Message sent. Thank you for reaching out.");
      setContact({ name: "", email: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCLoading(false); }
  };

  const field = "w-full bg-transparent border-b border-border px-0 py-3 text-bone placeholder:text-bone/30 outline-none focus:border-gold transition-colors duration-300";

  return (
    <div className="bg-ink min-h-screen">
      <Navbar />
      <AIChatWidget />

      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Family legacy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        </motion.div>
        <div className="relative max-w-7xl mx-auto px-6 pb-24 pt-40 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="overline text-gold mb-8">
            Estate Planning · Trusts · Legacy
          </motion.div>
          <h1 className="serif font-light tracking-tighter text-bone text-5xl sm:text-7xl lg:text-8xl leading-[0.95]">
            {heroLines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease, delay: 0.4 + i * 0.15 }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8, ease }} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 max-w-2xl">
            <p className="text-bone/70 text-lg leading-relaxed">A boutique estate planning practice devoted to safeguarding families and the legacies they leave behind.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8, ease }} className="mt-10">
            <a href="#booking" data-testid="hero-cta" className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-ink overline hover:bg-bone transition-colors duration-300">
              Book a Consultation <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-border/50 py-6 bg-[#0d0d0d]">
        <Marquee speed={28} gradient={false}>
          {["Wills", "Living Trusts", "Power of Attorney", "Probate", "Guardianship", "Asset Protection", "Legacy Planning"].map((t, i) => (
            <span key={i} className="serif text-3xl sm:text-4xl text-bone/40 mx-12 flex items-center gap-12">{t} <span className="text-gold text-xl">✦</span></span>
          ))}
        </Marquee>
      </div>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-28">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
            <div className="md:col-span-8">
              <div className="overline text-gold mb-5">What we do</div>
              <h2 className="serif font-light text-4xl sm:text-5xl lg:text-6xl text-bone tracking-tighter leading-tight">Comprehensive protection for every stage of life</h2>
            </div>
            <p className="md:col-span-4 text-bone/50 leading-relaxed">From your first will to complex multi-generational trusts, we build plans that endure.</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-px bg-border/50">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div data-testid={`service-${i}`} className="group bg-ink p-10 h-full hover:bg-[#0f0f0f] transition-colors duration-500">
                <s.icon size={40} weight="thin" className="text-gold mb-8" />
                <h3 className="serif text-3xl text-bone mb-4">{s.title}</h3>
                <p className="text-bone/50 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* APPROACH / MANIFESTO */}
      <section id="approach" className="bg-[#0d0d0d] border-y border-border/50 py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-16 items-start">
            <div className="md:col-span-5">
              <Reveal>
                <div className="overline text-gold mb-5">Our approach</div>
                <h2 className="serif font-light text-4xl sm:text-5xl text-bone tracking-tighter leading-tight mb-8">A practice built on trust, patience, and precision.</h2>
                <div className="overflow-hidden rounded-sm">
                  <img src={APPROACH_IMG} alt="Approach" className="w-full aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                </div>
              </Reveal>
            </div>
            <div className="md:col-span-6 md:col-start-7 space-y-14 md:pt-16">
              {chapters.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.1}>
                  <div className="grid grid-cols-12 gap-6 border-t border-border/50 pt-8">
                    <div className="col-span-3 serif text-6xl text-gold/70 font-light">{c.n}</div>
                    <div className="col-span-9">
                      <h3 className="serif text-3xl text-bone mb-3">{c.title}</h3>
                      <p className="text-bone/50 leading-relaxed">{c.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <Pricing />

      {/* BOOKING */}
      <section id="booking" className="max-w-7xl mx-auto px-6 py-28">
        <div className="grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <div className="overline text-gold mb-5">Book a consultation</div>
              <h2 className="serif font-light text-4xl sm:text-5xl text-bone tracking-tighter leading-tight mb-8">Begin your plan with a private conversation.</h2>
              <div className="overflow-hidden rounded-sm">
                <img src={DOC_IMG} alt="Signing" className="w-full aspect-[4/3] object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal delay={0.1}>
              <form onSubmit={submitBooking} data-testid="booking-form" className="space-y-7">
                <div className="grid sm:grid-cols-2 gap-7">
                  <input required data-testid="booking-name" placeholder="Full name" className={field} value={booking.name} onChange={(e) => setBooking({ ...booking, name: e.target.value })} />
                  <input required type="email" data-testid="booking-email" placeholder="Email" className={field} value={booking.email} onChange={(e) => setBooking({ ...booking, email: e.target.value })} />
                </div>
                <div className="grid sm:grid-cols-2 gap-7">
                  <input data-testid="booking-phone" placeholder="Phone (optional)" className={field} value={booking.phone} onChange={(e) => setBooking({ ...booking, phone: e.target.value })} />
                  <input required type="date" data-testid="booking-date" className={field} value={booking.preferred_date} onChange={(e) => setBooking({ ...booking, preferred_date: e.target.value })} />
                </div>
                <select data-testid="booking-service" className={`${field} appearance-none`} value={booking.service} onChange={(e) => setBooking({ ...booking, service: e.target.value })}>
                  {services.map((s) => <option key={s.title} className="bg-ink" value={s.title}>{s.title}</option>)}
                </select>
                <textarea data-testid="booking-message" rows={3} placeholder="Tell us briefly about your needs (optional)" className={field} value={booking.message} onChange={(e) => setBooking({ ...booking, message: e.target.value })} />
                {payCfg?.configured ? (
                  <div className="pt-2">
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-border/50">
                      <span className="text-bone/60 text-sm">Consultation fee</span>
                      <span className="serif text-2xl text-bone">$150 <span className="text-bone/30 text-sm">{payCfg.currency}</span></span>
                    </div>
                    <p className="text-bone/40 text-xs mb-5">Secure your slot by paying the one-hour consultation fee. Fully credited toward any package you choose.</p>
                    <BookingPayPal config={payCfg} getBooking={getBooking} onPaid={resetBooking} />
                  </div>
                ) : (
                  <button disabled={bLoading} data-testid="booking-submit" className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-ink overline hover:bg-bone transition-colors duration-300 disabled:opacity-50">
                    {bLoading ? "Sending…" : "Request Consultation"} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-[#0d0d0d] border-t border-border/50 py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <div className="overline text-gold mb-5">Get in touch</div>
            <h2 className="serif font-light text-4xl sm:text-5xl text-bone tracking-tighter mb-12">Have a question? We're listening.</h2>
            <form onSubmit={submitContact} data-testid="contact-form" className="space-y-7 text-left">
              <div className="grid sm:grid-cols-2 gap-7">
                <input required data-testid="contact-name" placeholder="Name" className={field} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                <input required type="email" data-testid="contact-email" placeholder="Email" className={field} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              </div>
              <textarea required data-testid="contact-message" rows={4} placeholder="Your message" className={field} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
              <div className="text-center">
                <button disabled={cLoading} data-testid="contact-submit" className="px-10 py-4 border border-gold text-gold overline hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50">
                  {cLoading ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
